import React, { useEffect, useState } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import DeleteModel from '../Common/DeleteModel';
import HealthInfoModal from './HealthInfoModal';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { saveProviderHealthInfo, deleteProviderHealthInfo, getAllProviderHealthInfo } from '../../Services/ProviderService';

const PROVIDER_ID_STORAGE_KEY = 'providerForm.providerId';
const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

const emptyHealthInfo = {
  test_type: '',
  completion_date: '',
  expiration_date: '',
  document: null,
  type: 'create',
};

export default function HealthInfo({ initialData, providerId, onSaveSuccess, onContinue }) {
  const { token } = useAuth();
  const [items, setItems] = useState(Array.isArray(initialData?.healthInfo) ? initialData.healthInfo : []);
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null, item: null, isLoading: false });

  const getProviderIdFromStorage = () => {
    try {
      return localStorage.getItem(PROVIDER_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read provider id from localStorage:', error);
      return null;
    }
  };

  const getOrganizationIdFromStorage = () => {
    try {
      return localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read organization id from localStorage:', error);
      return null;
    }
  };
  const refreshHealthInfo = async () => {
    const providerIdToUse = providerId || getProviderIdFromStorage();
    if (!token || !providerIdToUse) return;

    setLoading(true);
    try {
      const response = await getAllProviderHealthInfo(token, providerIdToUse);
      if (response?.is_success) {
        const refreshed = Array.isArray(response.data) ? response.data : [];
        setItems(refreshed);
        if (onSaveSuccess) onSaveSuccess(refreshed);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      }
    } catch (error) {
      console.error('Error loading health info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setItems(Array.isArray(initialData?.healthInfo) ? initialData.healthInfo : []);
  }, [initialData]);

  useEffect(() => {
    if (!token || !providerId) return;
    if (Array.isArray(initialData?.healthInfo) && initialData.healthInfo.length > 0) return;
    refreshHealthInfo();
  }, [token, providerId, initialData]);

  const handleAdd = () => {
    setEditingIndex(null);
    setShowModal(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingIndex(null);
  };

  const buildPayload = (values) => {
    const providerIdToUse = providerId || getProviderIdFromStorage();
    const organizationIdToUse = values.organization_id || getOrganizationIdFromStorage() || null;
    const isUpdate = Boolean(values.id || values.Id || values.health_info_id);

    return {
      ...values,
      provider_id: providerIdToUse,
      organization_id: organizationIdToUse,
      type: isUpdate ? 'update' : 'create',
    };
  };

  const handleSave = async (values) => {
    const payload = buildPayload(values);
    if (!payload.provider_id) {
      toast.error('Provider ID is required to save health info.');
      return;
    }

    setSaving(true);
    try {
      const response = await saveProviderHealthInfo(token, payload);
      if (response?.is_success) {
        await refreshHealthInfo();
        setShowModal(false);
        setEditingIndex(null);
        toast.success('Health info saved successfully.');
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to save health info.');
      }
    } catch (error) {
      console.error('Error saving health info:', error);
      toast.error(error?.message || 'Failed to save health info.');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (index) => {
    setDeleteModal({ isOpen: true, index, item: items[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.index === null || deleteModal.index === undefined) return;
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      await handleDelete(deleteModal.index);
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleDelete = async (index) => {
    const item = items[index];
    const optimisticUpdate = (updated) => {
      setItems(updated);
      if (onSaveSuccess) onSaveSuccess(updated);
    };

    if (!item || !(item.id || item.Id || item.health_info_id)) {
      const updated = items.filter((_, idx) => idx !== index);
      optimisticUpdate(updated);
      return;
    }

    setDeletingIndex(index);
    try {
      const id = item.id || item.Id || item.health_info_id;
      const response = await deleteProviderHealthInfo(token, id);
      if (response?.is_success) {
        const refreshed = await refreshHealthInfo();
        if (Array.isArray(refreshed)) {
          optimisticUpdate(refreshed);
        } else {
          const updated = items.filter((_, idx) => idx !== index);
          optimisticUpdate(updated);
        }
        toast.success('Health info deleted successfully.');
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to delete health info.');
      }
    } catch (error) {
      console.error('Error deleting health info:', error);
      toast.error(error?.message || 'Failed to delete health info.');
    } finally {
      setDeletingIndex(null);
    }
  };

  const handleContinueClick = () => {
    if (onContinue) onContinue();
  };

  return (
    <>
      <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold hc-text-primary m-0">Health Info</h6>
          <button
            type="button"
            className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
            onClick={handleAdd}
          >
            <MdAdd size={18} /> Add
          </button>
        </div>


        {loading ?(
          <div className="text-center py-4 text-muted small">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-4 text-muted small">
            No additional certificates added yet. Click "Add" to create one.
          </div>
        ) : (

        <div className="row g-3 mb-3">
          {
            items.map((item, index) => (
              <React.Fragment key={item.id || item.health_info_id || index}>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Test Type
                  </div>

                  <div className="fw-semibold text-dark">
                    {item.test_type_name || 'N/A'}
                  </div>

                </div>
              </div>
            </div>



            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Completion Date
                  </div>

                  <div className="fw-semibold text-dark">
                    {item.completion_date || 'N/A'}
                  </div>

                </div>
              </div>
            </div>



            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Expiration Date
                  </div>

                  <div className="fw-semibold text-dark">
                  {item.expiration_date || 'N/A'}
                  </div>

                </div>
              </div>
            </div>


            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Actions
                  </div>

                  <div className="d-flex gap-2">

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary rounded-3"
                      onClick={() => handleEdit(index)}
                      disabled={saving || deletingIndex === index}
                    >
                      <MdEdit size={16} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger rounded-3"
                      onClick={() => openDeleteModal(index)}
                      disabled={saving || deletingIndex === index}
                    >
                      <MdDelete size={16} />
                    </button>

                  </div>

                </div>
              </div>
            </div> 
              </React.Fragment>

            ))}
          </div>

          )}


        <HealthInfoModal
          isOpen={showModal}
          providerId={providerId}
          onClose={handleClose}
          onSave={handleSave}
          initialData={editingIndex !== null ? items[editingIndex] : null}
          isLoading={saving}
        />
      </div>

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel="health info record"
        itemName={deleteModal.item ? (deleteModal.item.test_type_name || deleteModal.item.test_type || 'this health info record') : 'this health info record'}
        description="This will permanently delete this health info record. This action cannot be undone."
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-sm hc-bg-primary text-white rounded-3 px-4 py-2"
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
    </>
  );
}
